import React, { useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { formatAccountCurrency, formatDate } from "../../../services/util";
import moment from "moment";
import {
  DatePicker,
  FormGroup,
  Input,
  Select,
  SubmitButton,
} from "../../../components/react-hook-form";
import { useParams } from "react-router";
import { useXeroClient } from "../../../hooks";
import { useQuery } from "react-query";
import { useMemo } from "react";
import { includes, orderBy } from "lodash";
import { Page } from "../../../components/common";
import {
  Alert,
  Button,
  Col,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { useForm, FormProvider } from "react-hook-form";
import numeral from "numeral";
import useModal from "../../../hooks/useModal";
import { NotificationManager } from "react-notifications";
import { Link } from "react-router-dom";

const defaultValues = {
  adjustmentType: "decrease",
  date: formatDate(moment()),
  adjustment: "0",
  costPrice: "0.00",
  newQuantity: "0",
  averageCost: "0.00",
  totalValue: "0.00",
  adjustmentValue: "0.00",
  quantityToday: "0.00",
  adjustmentAccount: null,
  notes: "",
};

export const AdjustInventory = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const methods = useForm({
    defaultValues,
    mode: "onBlur",
    shouldUnregister: false,
  });
  const { handleSubmit, watch, setValue, trigger, reset } = methods;
  const client = useXeroClient();
  const url = `items/${id}`;
  const { isOpen, setIsOpen, Modal } = useModal();

  const values = watch();
  const {
    adjustmentType,
    adjustmentValue,
    adjustment,
    adjustmentAccount,
    newQuantity,
    totalValue,
    costPrice,
    averageCost,
    date,
    notes,
  } = values;

  const { data: accounts } = useQuery("accounts", async () => {
    return await client
      .get("accounts")
      .get("data")
      .filter(
        (i) =>
          i.Type !== "BANK" &&
          (!i.SystemAccount ||
            includes(
              [
                "HISTORICAL",
                "REALISEDCURRENCYGAIN",
                "RETAINEDEARNINGS",
                "ROUNDING",
              ],
              i.SystemAccount
            ))
      )
      .map((i) => ({
        ...i,
        Description: `${i.Code} ${i.Name}`,
      }))
      .then((v) => orderBy(v, "Code"));
  });

  const {
    data: item,
    refetch,
    isPlaceholderData,
  } = useQuery(
    url,
    async () => {
      const item = await client.get(url).get("data");
      const { TotalCostPool, QuantityOnHand } = item;
      let averagePrice =
        QuantityOnHand * 1 ? TotalCostPool / QuantityOnHand : 0;
      // round the average cost down
      averagePrice = (Math.floor(averagePrice * 10000) / 10000).toFixed(4);
      item.AverageCost = averagePrice;
      item.TotalCostPool = (averagePrice * QuantityOnHand).toFixed(2);
      return item;
    },
    {
      placeholderData: {},
      onSuccess: (item) => {
        setValue("costPrice", item.AverageCost);
        setValue("averageCost", item.AverageCost);
        setValue("quantityToday", item.QuantityOnHand);
      },
    }
  );
  const {
    Name,
    Code,
    QuantityOnHand,
    TotalCostPool,
    AverageCost,
    InventoryAssetAccountCode,
    InventoryAssetAccountName,
  } = item;

  const adjustmentTypes = useMemo(
    () => [
      {
        label: t("Decrease quantity"),
        value: "decrease",
      },
      {
        label: t("Increase quantity"),
        value: "increase",
      },
      {
        label: t("Revaluation"),
        value: "revaluation",
      },
    ],
    [t]
  );

  useEffect(() => {
    if (adjustmentType === "decrease") {
      const newQuantity = Number(QuantityOnHand) - Number(adjustment);
      const adjustmentValue = adjustment * AverageCost * -1;
      setValue(
        "newQuantity",
        isNaN(newQuantity) ? "" : numeral(newQuantity).format("0[.]00[00]")
      );
      setValue("adjustmentValue", adjustmentValue);
      setValue(
        "impact",
        t(
          "Quantity on hand reduced by {{ adjustment }} (from {{ quantityOnHand }} to {{ newQuantity }}",
          {
            adjustment,
            quantityOnHand: QuantityOnHand,
            newQuantity,
          }
        )
      );
    } else if (adjustmentType === "increase") {
      const newQuantity = Number(QuantityOnHand) + Number(adjustment);
      const adjustmentValue = adjustment * costPrice;
      setValue(
        "newQuantity",
        isNaN(newQuantity) ? "" : numeral(newQuantity).format("0[.]00[00]")
      );
      setValue("adjustmentValue", adjustmentValue);
      setValue(
        "impact",
        t(
          "Quantity on hand increased by {{ adjustment }} (from {{ quantityOnHand }} to {{ newQuantity }}",
          {
            adjustment,
            quantityOnHand: QuantityOnHand,
            newQuantity,
          }
        )
      );
    } else if (adjustmentType === "revaluation") {
      const totalValue = QuantityOnHand * averageCost;
      const totalCostPool = QuantityOnHand * AverageCost;
      const adjustmentValue = Number(totalValue) - Number(totalCostPool);
      setValue("totalValue", isNaN(totalValue) ? "" : totalValue.toFixed(2));
      setValue("adjustmentValue", adjustmentValue);
    }
  }, [adjustmentType, QuantityOnHand, adjustment, costPrice, averageCost, t]);

  function getImpact() {
    if (adjustmentType === "decrease") {
      return t(
        "Quantity on hand reduced by {{ adjustment }} (from {{ quantityOnHand }} to {{ newQuantity }})",
        {
          adjustment,
          quantityOnHand: QuantityOnHand,
          newQuantity,
        }
      );
    } else if (adjustmentType === "increase") {
      return t(
        "Quantity on hand increased by {{ adjustment }} (from {{ quantityOnHand }} to {{ newQuantity }})",
        {
          adjustment,
          quantityOnHand: QuantityOnHand,
          newQuantity,
        }
      );
    } else if (adjustmentType === "revaluation") {
      if (totalValue > TotalCostPool) {
        return t(
          "Total item value increased from {{ totalCostPool }} to {{ totalValue }}",
          {
            totalCostPool: formatAccountCurrency(TotalCostPool),
            totalValue: formatAccountCurrency(totalValue),
          }
        );
      } else if (totalValue < TotalCostPool) {
        return t(
          "Total item value decreased from {{ totalCostPool }} to {{ totalValue }}",
          {
            totalCostPool: formatAccountCurrency(TotalCostPool),
            totalValue: formatAccountCurrency(totalValue),
          }
        );
      }
    }
  }

  async function onSubmit() {
    const documents = getDocuments();

    for (const i of documents) {
      if (i.Type === "ACCPAY") {
        await client.post("invoices", i);
      } else if (i.Type === "ACCPAYCREDIT") {
        await client.post("creditnotes", i);
      }
    }
    setIsOpen(false);
    await reset();
    await refetch();
    NotificationManager.success(t("Adjustment successful"));
  }

  function getDocuments() {
    if (adjustmentType === "increase") {
      return [
        {
          Type: "ACCPAY",
          Contact: {
            Name: "Inventory Adjustments",
          },
          Date: date,
          DueDate: date,
          LineAmountTypes: "NoTax",
          Status: "AUTHORISED",
          LineItems: [
            {
              ItemCode: Code,
              Description: Name || Code,
              Quantity: adjustment,
              UnitAmount: costPrice,
              AccountCode: InventoryAssetAccountCode,
            },
            {
              Description: "Inventory Adjustment",
              Quantity: adjustment,
              UnitAmount: -costPrice,
              AccountCode: adjustmentAccount.Code,
            },
          ],
        },
      ];
    } else if (adjustmentType === "decrease") {
      return [
        {
          Type: "ACCPAYCREDIT",
          Contact: {
            Name: "Inventory Adjustments",
          },
          Date: date,
          DueDate: date,
          LineAmountTypes: "NoTax",
          Status: "AUTHORISED",
          LineItems: [
            {
              ItemCode: Code,
              Description: Name || Code,
              Quantity: adjustment,
              UnitAmount: costPrice,
              AccountCode: InventoryAssetAccountCode,
            },
            {
              Description: "Inventory Adjustment",
              Quantity: adjustment,
              UnitAmount: -costPrice,
              AccountCode: adjustmentAccount.Code,
            },
          ],
        },
      ];
    } else if (adjustmentType === "revaluation") {
      return [
        {
          Type: "ACCPAYCREDIT",
          Contact: {
            Name: "Inventory Adjustments",
          },
          Date: date,
          DueDate: date,
          LineAmountTypes: "NoTax",
          Status: "AUTHORISED",
          LineItems: [
            {
              ItemCode: Code,
              Description: Name || Code,
              Quantity: QuantityOnHand,
              UnitAmount: AverageCost,
              AccountCode: InventoryAssetAccountCode,
            },
            {
              Description: "Inventory Adjustment",
              Quantity: QuantityOnHand,
              UnitAmount: AverageCost,
              AccountCode: adjustmentAccount.Code,
            },
          ],
        },
        {
          Type: "ACCPAY",
          Contact: {
            Name: "Inventory Adjustments",
          },
          Date: date,
          DueDate: date,
          LineAmountTypes: "NoTax",
          Status: "AUTHORISED",
          LineItems: [
            {
              ItemCode: Code,
              Description: Name || Code,
              Quantity: QuantityOnHand,
              UnitAmount: averageCost,
              AccountCode: InventoryAssetAccountCode,
            },
            {
              Description: "Inventory Adjustment",
              Quantity: QuantityOnHand,
              UnitAmount: -averageCost,
              AccountCode: adjustmentAccount.Code,
            },
          ],
        },
      ];
    }
  }

  if (isPlaceholderData) return null;

  const base = (children) => (
    <Page title={t("New Adjustment for {{ Name }} ", { Name: Name || Code })}>
      {children}
    </Page>
  );

  if (!item.IsTrackedAsInventory) {
    return base(
      <Alert color="danger">
        <Trans>This item is not tracked.</Trans>
      </Alert>
    );
  }

  return base(
    <FormProvider {...methods}>
      {isOpen ? (
        <Modal>
          <ModalHeader>
            <Trans>Review Adjustment</Trans>
          </ModalHeader>
          <ModalBody>
            <FormGroup labelClassName="tw-font-bold" label={t("Date")}>
              <p>{date}</p>
            </FormGroup>
            <FormGroup labelClassName="tw-font-bold" label={t("Impact")}>
              <p>{getImpact()}</p>
            </FormGroup>
            <Table size="sm">
              <thead>
                <tr>
                  <th>
                    <Trans>Account</Trans>
                  </th>
                  <th className="text-right">
                    <Trans>Adjustment</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {InventoryAssetAccountCode} {InventoryAssetAccountName}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(adjustmentValue)}
                  </td>
                </tr>
                <tr>
                  <td>
                    {adjustmentAccount.Code} {adjustmentAccount.Name}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(adjustmentValue * -1)}
                  </td>
                </tr>
              </tbody>
            </Table>
            <FormGroup labelClassName="tw-font-bold" label={t("Notes")}>
              <p>{notes}</p>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="link" onClick={() => setIsOpen(false)}>
              <Trans>Close</Trans>
            </Button>
            <SubmitButton onClick={handleSubmit(onSubmit)}>
              <Trans>Post Adjustment</Trans>
            </SubmitButton>
          </ModalFooter>
        </Modal>
      ) : null}

      <Row form>
        <Col>
          <FormGroup label={t("Adjustment type")} name="adjustmentType">
            <Select
              name="adjustmentType"
              simpleValue
              options={adjustmentTypes}
              clearable={false}
              rules={{ required: true }}
            />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup label={t("Date")} name="date">
            <DatePicker
              rules={{ required: true }}
              name="date"
              className="form-control"
            />
          </FormGroup>
        </Col>
      </Row>
      <hr />
      <Row form>
        <Col>
          <FormGroup label={t("Qty Today")}>
            <Input name="quantityToday" readOnly className="form-control" />
          </FormGroup>
        </Col>
        <Col xs="auto" className="tw-self-end pb-4">
          {adjustmentType === "increase" ? "+" : ""}
          {adjustmentType === "decrease" ? "-" : ""}
          {adjustmentType === "revaluation" ? "x" : ""}
        </Col>
        <Col>
          {adjustmentType === "increase" || adjustmentType === "decrease" ? (
            <FormGroup label={t("Adjustment")} name="adjustment">
              <Input
                type="number"
                rules={{ required: true, min: "0.0001" }}
                name="adjustment"
                className="form-control"
              />
            </FormGroup>
          ) : null}
          {adjustmentType === "revaluation" ? (
            <FormGroup label={t("Average Cost")} name="averageCost">
              <Input
                type="number"
                rules={{ required: true, min: 0 }}
                name="averageCost"
                className="form-control"
              />
            </FormGroup>
          ) : null}
        </Col>
        <Col xs="auto" className="tw-self-end pb-4">
          {adjustmentType === "decrease" || adjustmentType === "revaluation"
            ? "="
            : ""}
          {adjustmentType === "increase" ? "@" : ""}
        </Col>
        {adjustmentType === "increase" ? (
          <Col>
            <FormGroup label={t("Cost Price")} name="costPrice">
              <Input
                type="number"
                rules={{ required: true, min: 0 }}
                name="costPrice"
                className="form-control"
              />
            </FormGroup>
          </Col>
        ) : null}
        <Col>
          {adjustmentType === "decrease" || adjustmentType === "increase" ? (
            <FormGroup label={t("New qty on hand")} name="newQuantity">
              <Input
                type="number"
                rules={{ required: true, min: 0 }}
                className="form-control"
                name="newQuantity"
                readOnly
              />
            </FormGroup>
          ) : null}
          {adjustmentType === "revaluation" ? (
            <FormGroup label={t("Total Value")} name="totalValue">
              <Input
                type="number"
                rules={{ required: true, min: 0 }}
                className="form-control"
                name="totalValue"
                readOnly
              />
            </FormGroup>
          ) : null}
        </Col>
      </Row>
      <p className="text-muted">
        <Trans>
          Value of adjustment:{" "}
          {{
            adjustmentValue: formatAccountCurrency(Math.abs(adjustmentValue)),
          }}
        </Trans>
      </p>
      <FormGroup label={t("Adjustment Account")} name="adjustmentAccount">
        <Select
          options={accounts}
          labelKey="Description"
          valueKey="Code"
          name="adjustmentAccount"
          rules={{ required: true }}
        />
      </FormGroup>
      <FormGroup label={t("Notes")} name="notes">
        <Input
          placeholder={t("Reasons for adjustment")}
          name="notes"
          component="textarea"
          rules={{ required: true }}
          className="form-control"
        />
      </FormGroup>
      <div className="tw-flex tw-justify-end">
        <Link to={`/inventory/${item.ItemID}`} className="btn btn-link mr-2">
          Cancel
        </Link>
        <Button
          color="primary"
          onClick={async () => {
            if (await trigger()) {
              setIsOpen(true);
            }
          }}
        >
          {t("Review Adjustment")}
        </Button>
      </div>
    </FormProvider>
  );
};
